import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Save, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, TransformControls, Grid, Environment, ContactShadows } from '@react-three/drei';
import GenericModel from '@/components/GenericModel';

const ModelEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [model, setModel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    // Editor State
    const [activeTab, setActiveTab] = useState('transform'); // transform, material, lighting

    useEffect(() => {
        const fetchModel = async () => {
            try {
                // Try fetching specific model, or fall back to list
                try {
                    const { data } = await api.get(`/models/${id}`);
                    setModel(data);
                } catch (e) {
                    const { data } = await api.get('/models');
                    const found = data.find(m => m._id === id);
                    if (found) setModel(found);
                }
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };
        fetchModel();
    }, [id]);

    const handleSave = async () => {
        try {
            setMessage('Saving...');
            await api.put(`/models/${id}`, model);
            setMessage('Saved successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Error saving model');
        }
    };

    const updateNested = (section, field, value) => {
        setModel(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin mr-2" /> Loading...</div>;
    if (!model) return <div className="p-8">Model not found</div>;

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b px-6 py-3 flex justify-between items-center shadow-sm z-10">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/models')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="font-bold text-lg text-gray-900">{model.name}</h1>
                        <p className="text-xs text-gray-500">Edit Mode</p>
                    </div>
                </div>
                <div className="flex gap-2 items-center">
                    {message && <span className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'} mr-2`}>{message}</span>}
                    <button onClick={handleSave} className="flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-all shadow-sm">
                        <Save className="w-4 h-4 mr-2" /> Save Changes
                    </button>
                </div>
            </div>

            {/* Main Editor Area */}
            <div className="flex flex-1 overflow-hidden relative">

                {/* 3D Canvas */}
                <div className="flex-1 bg-gray-200 relative h-full w-full">
                    <Canvas shadows camera={{ position: [5, 5, 5], fov: 50 }}>
                        <color attach="background" args={['#f0f0f0']} />

                        {/* Lighting - Helper Driven by Props if we wanted, but standard studio for now is best for editing */}
                        <ambientLight intensity={model.lighting?.ambientIntensity || 0.5} />
                        <directionalLight
                            position={[
                                model.lighting?.directionalPosition?.x || 5,
                                model.lighting?.directionalPosition?.y || 10,
                                model.lighting?.directionalPosition?.z || 7
                            ]}
                            intensity={model.lighting?.directionalIntensity || 2.5}
                            castShadow
                            shadow-bias={-0.0001}
                        />
                        <Environment preset="city" />

                        {/* Helpers */}
                        <Grid infiniteGrid fadeDistance={50} sectionColor="#666" cellColor="#ccc" />
                        <ContactShadows resolution={1024} scale={20} blur={2} opacity={0.5} far={10} color="#000000" />

                        <Suspense fallback={null}>
                            {model.path ? (
                                <GenericModel
                                    url={`${import.meta.env.VITE_API_URL.replace('/api', '')}${model.path}`}
                                    scale={model.scale}
                                    rotation={model.rotation}
                                    position={model.position}
                                    materialConfig={model.material}
                                />
                            ) : null}
                        </Suspense>

                        <OrbitControls makeDefault />
                    </Canvas>

                    <div className="absolute top-4 left-4 bg-white/80 backdrop-blur p-2 rounded text-xs text-gray-600 pointer-events-none">
                        Use Left Click to Rotate • Right Click to Pan/Zoom
                    </div>
                </div>

                {/* Sidebar Controls */}
                <div className="w-80 bg-white border-l h-full overflow-y-auto flex flex-col shadow-xl z-20">
                    {/* Tabs */}
                    <div className="flex border-b text-sm font-medium">
                        <button onClick={() => setActiveTab('transform')} className={`flex-1 py-3 text-center ${activeTab === 'transform' ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-gray-700'}`}>Transform</button>
                        <button onClick={() => setActiveTab('material')} className={`flex-1 py-3 text-center ${activeTab === 'material' ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-gray-700'}`}>Material</button>
                        <button onClick={() => setActiveTab('lighting')} className={`flex-1 py-3 text-center ${activeTab === 'lighting' ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-gray-700'}`}>Lighting</button>
                    </div>


                    <div className="p-6 space-y-6 flex-1 overflow-y-auto">

                        {/* GENERAL SETTINGS - Always visible or in a new tab? Let's put it at top of Transform for now or new tab. Let's add a 'General' tab. */}

                        {activeTab === 'transform' && (
                            <div className="space-y-6">
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">General Info</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Title</label>
                                            <input
                                                type="text"
                                                value={model.title || ''}
                                                onChange={(e) => setModel({ ...model, title: e.target.value })}
                                                className="w-full border rounded p-2 text-sm"
                                                placeholder="Model Title"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Description</label>
                                            <textarea
                                                value={model.description || ''}
                                                onChange={(e) => setModel({ ...model, description: e.target.value })}
                                                className="w-full border rounded p-2 text-sm h-20"
                                                placeholder="Model Description"
                                            />
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Scale</h3>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['x', 'y', 'z'].map(axis => (
                                            <div key={axis}>
                                                <label className="block text-xs text-gray-500 mb-1 uppercase">{axis}</label>
                                                <input
                                                    type="number" step="0.1"
                                                    value={model.scale?.[axis] || 1}
                                                    onChange={(e) => updateNested('scale', axis, parseFloat(e.target.value))}
                                                    className="w-full border rounded p-2 text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-2 text-xs text-blue-600 flex items-center cursor-pointer" onClick={() => setModel({ ...model, scale: { x: 1, y: 1, z: 1 } })}>
                                        <RefreshCw className="w-3 h-3 mr-1" /> Reset Scale
                                    </div>
                                </section>
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Rotation</h3>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['x', 'y', 'z'].map(axis => (
                                            <div key={axis}>
                                                <label className="block text-xs text-gray-500 mb-1 uppercase">{axis}</label>
                                                <input
                                                    type="number" step="0.1"
                                                    value={model.rotation?.[axis] || 0}
                                                    onChange={(e) => updateNested('rotation', axis, parseFloat(e.target.value))}
                                                    className="w-full border rounded p-2 text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Position</h3>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['x', 'y', 'z'].map(axis => (
                                            <div key={axis}>
                                                <label className="block text-xs text-gray-500 mb-1 uppercase">{axis}</label>
                                                <input
                                                    type="number" step="0.1"
                                                    value={model.position?.[axis] || 0}
                                                    onChange={(e) => updateNested('position', axis, parseFloat(e.target.value))}
                                                    className="w-full border rounded p-2 text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* MATERIAL TAB */}
                        {activeTab === 'material' && (
                            <div className="space-y-6">
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Color</h3>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={model.material?.color || '#ffffff'}
                                            onChange={(e) => updateNested('material', 'color', e.target.value)}
                                            className="h-10 w-10 p-0 border-0 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={model.material?.color || '#ffffff'}
                                            onChange={(e) => updateNested('material', 'color', e.target.value)}
                                            className="flex-1 border rounded p-2 text-sm font-mono uppercase"
                                        />
                                    </div>
                                </section>
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 opacity-90">Properties</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex justify-between mb-1">
                                                <label className="text-xs text-gray-500">Metalness</label>
                                                <span className="text-xs font-mono">{model.material?.metalness || 0}</span>
                                            </div>
                                            <input
                                                type="range" min="0" max="1" step="0.01"
                                                value={model.material?.metalness || 0}
                                                onChange={(e) => updateNested('material', 'metalness', parseFloat(e.target.value))}
                                                className="w-full"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex justify-between mb-1">
                                                <label className="text-xs text-gray-500">Roughness</label>
                                                <span className="text-xs font-mono">{model.material?.roughness || 0}</span>
                                            </div>
                                            <input
                                                type="range" min="0" max="1" step="0.01"
                                                value={model.material?.roughness || 0.5}
                                                onChange={(e) => updateNested('material', 'roughness', parseFloat(e.target.value))}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* LIGHTING TAB */}
                        {activeTab === 'lighting' && (
                            <div className="space-y-6">
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Environment</h3>
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <label className="text-xs text-gray-500">Ambient Intensity</label>
                                            <span className="text-xs font-mono">{model.lighting?.ambientIntensity || 0.5}</span>
                                        </div>
                                        <input
                                            type="range" min="0" max="3" step="0.1"
                                            value={model.lighting?.ambientIntensity || 0.5}
                                            onChange={(e) => updateNested('lighting', 'ambientIntensity', parseFloat(e.target.value))}
                                            className="w-full"
                                        />
                                    </div>
                                </section>
                                <section>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Directional Light</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex justify-between mb-1">
                                                <label className="text-xs text-gray-500">Intensity</label>
                                                <span className="text-xs font-mono">{model.lighting?.directionalIntensity || 2.5}</span>
                                            </div>
                                            <input
                                                type="range" min="0" max="10" step="0.1"
                                                value={model.lighting?.directionalIntensity || 2.5}
                                                onChange={(e) => updateNested('lighting', 'directionalIntensity', parseFloat(e.target.value))}
                                                className="w-full"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs text-gray-500 mb-2">Position</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                <input
                                                    type="number" step="0.5"
                                                    value={model.lighting?.directionalPosition?.x || 5}
                                                    onChange={(e) => {
                                                        const newPos = { ...(model.lighting?.directionalPosition || { x: 5, y: 10, z: 7 }), x: parseFloat(e.target.value) };
                                                        updateNested('lighting', 'directionalPosition', newPos);
                                                    }}
                                                    className="w-full border rounded p-1 text-sm text-center"
                                                />
                                                <input
                                                    type="number" step="0.5"
                                                    value={model.lighting?.directionalPosition?.y || 10}
                                                    onChange={(e) => {
                                                        const newPos = { ...(model.lighting?.directionalPosition || { x: 5, y: 10, z: 7 }), y: parseFloat(e.target.value) };
                                                        updateNested('lighting', 'directionalPosition', newPos);
                                                    }}
                                                    className="w-full border rounded p-1 text-sm text-center"
                                                />
                                                <input
                                                    type="number" step="0.5"
                                                    value={model.lighting?.directionalPosition?.z || 7}
                                                    onChange={(e) => {
                                                        const newPos = { ...(model.lighting?.directionalPosition || { x: 5, y: 10, z: 7 }), z: parseFloat(e.target.value) };
                                                        updateNested('lighting', 'directionalPosition', newPos);
                                                    }}
                                                    className="w-full border rounded p-1 text-sm text-center"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModelEditor;
